import { activeNavItemAtom } from '@/global-store';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import SidebarNavButton from '@layout/Sidebar/MenuList/SidebarNavButton.tsx';
import NavItemLabel from '@layout/Sidebar/MenuList/NavItemLabel.tsx';
import { useBadgeVisibility } from '@layout/Sidebar/MenuList/useBadgeVisibility.ts';
import { useSidebar } from '@layout/Sidebar/SidebarContext.ts';
import type { MenuItem } from '@layout/Sidebar/MenuList/types.ts';

interface NavItemProps {
  item: MenuItem;
  level?: number;
  collapsibleTitle?: string;
}

function NavItem({ item, level = 0, collapsibleTitle = '' }: NavItemProps) {
  const { t } = useTranslation();
  const [activeNavItem, setActiveNavItem] = useAtom(activeNavItemAtom);
  const { isMobile, setOpenMobile } = useSidebar();
  const location = useLocation();
  const { pathname = '' } = location;
  const search = location.search ?? '';

  const selected = activeNavItem === item.path;
  const isExternal = Boolean(item.handle?.external);

  const activePaths = item.handle?.activeOnPaths ?? [];
  const inactivePaths = item.handle?.inactiveOnPaths ?? [];

  const activeQueryIncludes = item.handle?.activeOnQueryIncludes ?? [];
  const inactiveQueryIncludes = item.handle?.inactiveOnQueryIncludes ?? [];

  useEffect(() => {
    const matchesPath =
      item.path === '/' ? pathname === '/' : pathname.includes(item.path);
    const matchesActivePaths = activePaths.some((path) =>
      pathname.startsWith(path),
    );
    const matchesInactivePaths = inactivePaths.some((path) =>
      pathname.startsWith(path),
    );
    const matchesActiveQuery = activeQueryIncludes.some((part) =>
      search.includes(part),
    );
    const matchesInactiveQuery = inactiveQueryIncludes.some((part) =>
      search.includes(part),
    );

    const shouldActivate =
      matchesActiveQuery ||
      ((matchesPath || matchesActivePaths) &&
        !matchesInactivePaths &&
        !matchesInactiveQuery);

    if (shouldActivate) {
      setActiveNavItem(item.path);
    }
  }, [
    pathname,
    search,
    item.path,
    setActiveNavItem,
    activePaths.join('|'),
    inactivePaths.join('|'),
    activeQueryIncludes.join('|'),
    inactiveQueryIncludes.join('|'),
  ]);

  const showBadge = useBadgeVisibility(item.handle?.badge);
  const badgeText =
    typeof item.handle?.badge?.text === 'string' ? item.handle.badge.text : '';

  const title = item.handle?.i18nNameKey
    ? t(item.handle.i18nNameKey)
    : (item.handle?.name ?? '');
  const tooltipContent = item.handle?.i18nTooltipKey
    ? t(item.handle.i18nTooltipKey)
    : undefined;

  return (
    <SidebarNavButton
      component={isExternal ? 'a' : Link}
      target={item.target ? '_blank' : '_self'}
      to={isExternal ? undefined : item.path}
      href={isExternal ? item.path : undefined}
      rel={isExternal ? 'noreferrer' : undefined}
      level={level}
      selected={selected}
      aria-label={
        (collapsibleTitle ? `${collapsibleTitle} - ` : '') +
        title +
        (showBadge && badgeText ? ` - ${badgeText}` : '')
      }
      role='link'
      aria-current={selected ? 'page' : undefined}
      disabled={item.disabled}
      onClick={() => {
        setActiveNavItem(item.path);
        if (isMobile) {
          setOpenMobile(false);
        }
      }}
      tooltip={tooltipContent}
    >
      <NavItemLabel item={item} title={title} />
    </SidebarNavButton>
  );
}

export default NavItem;
